// =============================================================================
// Origin UI — src/pages/admin/OfficeDetailAdminTab.tsx
// Admin Config sub-tab for managing office details used in letter generation.
// Lists all 7 offices with entity names, addresses, signatories.
// Click to expand and edit any office.
// =============================================================================

import { useState, useEffect } from 'react';
import { letterApi, OfficeDetail } from '@/services/letterApi';
import { Card, ErrorBanner, LoadingSpinner } from '@/components/common';
import styles from './admin.module.css';

export function OfficeDetailAdminTab() {
  const [offices, setOffices] = useState<OfficeDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<Partial<OfficeDetail>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setLoading(true);
      setError('');
      const data = await letterApi.getAllOfficeDetails();
      setOffices(data);
    } catch (e: any) {
      setError(e.message || 'Failed to load office details');
    } finally {
      setLoading(false);
    }
  }

  function startEdit(office: OfficeDetail) {
    setEditingId(office.officeLocationId);
    setForm({
      legalEntityName: office.legalEntityName || '',
      registeredAddress: office.registeredAddress || '',
      phone: office.phone || '',
      fax: office.fax || '',
      website: office.website || '',
      letterheadType: office.letterheadType || 'Dubai',
      stampBlobPath: office.stampBlobPath || '',
      businessRegistrationNo: office.businessRegistrationNo || '',
      defaultSignatory1Name: office.defaultSignatory1Name || '',
      defaultSignatory1Title: office.defaultSignatory1Title || '',
      defaultSignatory2Name: office.defaultSignatory2Name || '',
      defaultSignatory2Title: office.defaultSignatory2Title || '',
    });
    setSuccess('');
  }

  function cancelEdit() {
    setEditingId(null);
    setForm({});
  }

  async function save() {
    if (editingId === null) return;
    try {
      setSaving(true);
      setError('');
      await letterApi.updateOfficeDetail(editingId, form);
      setEditingId(null);
      setForm({});
      setSuccess('Office details saved successfully.');
      setTimeout(() => setSuccess(''), 3000);
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      {success && (
        <div style={{ padding: '10px 16px', marginBottom: 16, borderRadius: 6, background: 'var(--green-light)', color: 'var(--green)', fontSize: 13, fontWeight: 600 }}>
          {success}
        </div>
      )}

      <div className={styles.toolBar}>
        <span className={styles.count}>{offices.length} offices configured</span>
      </div>

      <p className={styles.muted} style={{ marginBottom: 16 }}>
        Office details are used in letter generation — entity names, addresses, signatories, and letterhead/stamp configuration.
        Click an office to edit its details.
      </p>

      {offices.map(office => (
        <div key={office.officeLocationId} style={{ marginBottom: 12 }}>
        <Card className={styles.formCard} padding={true}>
          {/* Summary row */}
          <div
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', cursor: 'pointer' }}
            onClick={() => editingId === office.officeLocationId ? cancelEdit() : startEdit(office)}
          >
            <div>
              <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 15 }}>{office.officeName}</div>
              <div style={{ color: 'var(--gray-500)', fontSize: 13, marginTop: 2 }}>{office.legalEntityName}</div>
              {office.registeredAddress && (
                <div style={{ color: 'var(--gray-400)', fontSize: 12, marginTop: 2 }}>{office.registeredAddress}</div>
              )}
              {!office.registeredAddress && (
                <div style={{ color: 'var(--orange)', fontSize: 12, marginTop: 2, fontStyle: 'italic' }}>Address not configured</div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <span style={{
                padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                background: 'var(--turquoise-light)', color: 'var(--turquoise-dark)',
              }}>
                {office.letterheadType}
              </span>
              <span style={{ color: 'var(--gray-400)', fontSize: 18 }}>
                {editingId === office.officeLocationId ? '▲' : '▼'}
              </span>
            </div>
          </div>

          {/* Edit form (expanded) */}
          {editingId === office.officeLocationId && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--gray-200)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Legal Entity Name *" value={form.legalEntityName || ''} onChange={v => setForm({ ...form, legalEntityName: v })} />
                <Field label="Letterhead Type" value={form.letterheadType || ''} onChange={v => setForm({ ...form, letterheadType: v })} placeholder="Dubai / Oman / Singapore" />
                <div style={{ gridColumn: '1 / -1' }}>
                  <Field label="Registered Address" value={form.registeredAddress || ''} onChange={v => setForm({ ...form, registeredAddress: v })} placeholder="Full postal address" />
                </div>
                <Field label="Phone" value={form.phone || ''} onChange={v => setForm({ ...form, phone: v })} placeholder="+971 4 428 1888" />
                <Field label="Fax" value={form.fax || ''} onChange={v => setForm({ ...form, fax: v })} placeholder="+971 4 363 7468" />
                <Field label="Website" value={form.website || ''} onChange={v => setForm({ ...form, website: v })} placeholder="www.oqt.com" />
                <Field label="Business Reg No." value={form.businessRegistrationNo || ''} onChange={v => setForm({ ...form, businessRegistrationNo: v })} placeholder="e.g. 201104214W" />
                <Field label="Stamp Blob Path" value={form.stampBlobPath || ''} onChange={v => setForm({ ...form, stampBlobPath: v })} placeholder="stamps/oqt-limited.png" />
              </div>

              <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--gray-100)' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)', marginBottom: 8 }}>Default Signatories</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Signatory 1 — Name" value={form.defaultSignatory1Name || ''} onChange={v => setForm({ ...form, defaultSignatory1Name: v })} placeholder="Shannon Naudé" />
                  <Field label="Signatory 1 — Title" value={form.defaultSignatory1Title || ''} onChange={v => setForm({ ...form, defaultSignatory1Title: v })} placeholder="Head of Human Resources" />
                  <Field label="Signatory 2 — Name" value={form.defaultSignatory2Name || ''} onChange={v => setForm({ ...form, defaultSignatory2Name: v })} placeholder="Rodney Mugwendere" />
                  <Field label="Signatory 2 — Title" value={form.defaultSignatory2Title || ''} onChange={v => setForm({ ...form, defaultSignatory2Title: v })} placeholder="Human Resources Generalist" />
                </div>
              </div>

              <div className={styles.formActions} style={{ marginTop: 16 }}>
                <button className={styles.cancelBtn} onClick={cancelEdit}>Cancel</button>
                <button className={styles.saveBtn} onClick={save} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}
        </Card>
        </div>
      ))}
    </div>
  );
}

function Field({ label, value, onChange, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <div className={styles.field}>
      <label className={styles.fieldLabel}>{label}</label>
      <input
        className={styles.fieldInput}
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}
